import React, { useEffect, useState } from "react";
import GlobalHeader from "../../../components/GlobalHeader";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FaWhatsapp } from "react-icons/fa";
import useApi from "../../../hooks/useApi";
import useSnack from "../../../hooks/useSnack";
import SnackAlert from "../../../components/SnackAlert";
import WhatsappConfig from "../../../components/WhatsappConfig";

const ROL_LABELS = { artista: "Artista", productor: "Difusor" };

const WhatsappAdmin = () => {
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const { loadApi, loadingApi } = useApi();
  const [estado, setEstado] = useState({ conectado: false, qr: null });
  const [contactos, setContactos] = useState([]);
  const [destinatario, setDestinatario] = useState(null);
  const [mensaje, setMensaje] = useState("");

  const cargarEstado = () => {
    loadApi("admin/whatsapp/estado", true, "get")
      .then((res) => setEstado(res.data))
      .catch((e) => showSnack(e.message, "error"));
  };

  useEffect(() => {
    cargarEstado();
    loadApi("admin/whatsapp/contactos", true, "get")
      .then((res) => setContactos(res.data || []))
      .catch(() => {});
  }, []);

  // Mientras no esté conectado, refrescar el estado cada 5s (el QR rota)
  useEffect(() => {
    if (estado.conectado) return;
    const timer = setInterval(cargarEstado, 5000);
    return () => clearInterval(timer);
  }, [estado.conectado]);

  const enviarMensaje = () => {
    if (!destinatario) {
      showSnack("Elige un destinatario", "warning");
      return;
    }
    if (!mensaje.trim()) {
      showSnack("Escribe el mensaje", "warning");
      return;
    }
    loadApi("admin/whatsapp/mensaje", true, "post", {
      userId: destinatario.userId,
      mensaje: mensaje.trim(),
    })
      .then((res) => {
        setMensaje("");
        showSnack(res.data.message);
      })
      .catch((e) => {
        showSnack(e.message, "error");
      });
  };

  return (
    <>
      <GlobalHeader />
      <Container maxWidth="md" sx={{ pb: 6 }}>
        <Stack gap={3} pt={2}>
          <Stack direction="row" alignItems="center" gap={1}>
            <FaWhatsapp color="#25D366" fontSize="34px" />
            <Typography variant="h4" color="primary.main" fontWeight="900">
              WhatsApp
            </Typography>
            <Chip
              label={estado.conectado ? "Conectado" : "Desconectado"}
              color={estado.conectado ? "success" : "error"}
              size="small"
            />
          </Stack>

          {/* Vinculación */}
          {!estado.conectado && (
            <Stack gap={2} alignItems="flex-start">
              <Alert severity="warning" sx={{ width: "100%" }}>
                El número emisor no está vinculado. Usa un número{" "}
                <strong>dedicado</strong> (no el personal): en el teléfono abre
                WhatsApp → Ajustes → <strong>Dispositivos vinculados</strong> →{" "}
                <strong>Vincular un dispositivo</strong> y escanea el código.
              </Alert>
              {estado.qr ? (
                <Box
                  component="img"
                  src={estado.qr}
                  alt="Código QR para vincular WhatsApp"
                  sx={{ width: 260, height: 260, border: "1px solid #ddd" }}
                />
              ) : (
                <Typography variant="body2" color="GrayText">
                  Generando código QR… esta página se actualiza sola cada 5
                  segundos.
                </Typography>
              )}
              <Button variant="outlined" onClick={cargarEstado}>
                Actualizar estado
              </Button>
            </Stack>
          )}

          {estado.conectado && (
            <Alert severity="success">
              WhatsApp conectado: los avisos automáticos (canción a revisar,
              aprobaciones) se están enviando a los usuarios que activaron el
              servicio en su perfil.
            </Alert>
          )}

          {/* Avisos internos del equipo: el admin registra aquí su número */}
          <WhatsappConfig
            showSnack={showSnack}
            titulo="Mis avisos del equipo"
            descripcion="Registra tu número para recibir por WhatsApp los avisos internos: cada registro nuevo y cada difusor que queda pendiente de verificación. Solo se envían si activas el switch."
          />

          <Divider />

          {/* Mensaje dedicado */}
          <Typography variant="h5">Enviar mensaje dedicado</Typography>
          <Typography variant="body2" color="GrayText">
            Solo aparecen los usuarios que registraron su número y activaron
            los avisos por WhatsApp ({contactos.length} disponibles).
          </Typography>
          <Autocomplete
            options={contactos}
            value={destinatario}
            onChange={(e, value) => setDestinatario(value)}
            getOptionLabel={(c) =>
              `${c.nombre} (${c.email}) — ${ROL_LABELS[c.rol] || c.rol}`
            }
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            noOptionsText="Nadie ha activado los avisos todavía"
            renderInput={(params) => (
              <TextField {...params} label="Destinatario" />
            )}
            sx={{ maxWidth: "520px" }}
          />
          <TextField
            multiline
            rows={4}
            label="Mensaje"
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            placeholder="Escribe el mensaje que recibirá por WhatsApp..."
          />
          <Stack direction="row">
            <LoadingButton
              loading={loadingApi.includes("post__admin/whatsapp/mensaje")}
              onClick={enviarMensaje}
              disabled={!estado.conectado}
            >
              Enviar por WhatsApp
            </LoadingButton>
          </Stack>
        </Stack>
      </Container>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
};

export default WhatsappAdmin;
